import React from 'react';
import { X, Play, BookOpen } from 'lucide-react';
import { Exercise } from '../types/dataModel';
import { isYouTubeUrl, getYouTubeEmbedUrl } from '../utils/youtubeUtils';

interface ExerciseDetailProps {
  exercise: Exercise;
  onClose: () => void;
}

const ExerciseDetail: React.FC<ExerciseDetailProps> = ({ exercise, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center md:p-4 z-50">
      {/* Mobile: Full-page modal, Desktop: Centered overlay */}
      <div className="bg-white w-full h-full md:h-auto md:max-h-[90vh] md:max-w-4xl md:rounded-xl md:overflow-hidden flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 md:p-6 md:rounded-t-xl">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">{exercise.name}</h2>
                {exercise.muscleGroup && (
                  <div className="flex items-center gap-2 mt-1">
                    <span className="px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium bg-blue-100 text-blue-800">
                      {exercise.muscleGroup}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5 md:w-6 md:h-6 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 md:p-6 space-y-4 md:space-y-6 overflow-y-auto flex-1">
          {/* Exercise Image */}
          {exercise.image && (
            <div className="w-full h-48 md:h-64 overflow-hidden rounded-lg">
              <img 
                src={exercise.image} 
                alt={exercise.name}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Description */}
          {exercise.description && (
            <div>
              <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <BookOpen className="w-4 h-4 md:w-5 md:h-5 text-primary-600" />
                Description
              </h3>
              <p className="text-sm md:text-base text-gray-600 whitespace-pre-line">{exercise.description}</p>
            </div>
          )}

          {/* Video */}
          {exercise.video && (
            <div>
              <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Play className="w-4 h-4 md:w-5 md:h-5 text-primary-600" />
                Video
              </h3>
              <div className="aspect-video">
                {isYouTubeUrl(exercise.video) ? (
                  <iframe
                    src={getYouTubeEmbedUrl(exercise.video) || ''}
                    title={exercise.name}
                    className="w-full h-full rounded-lg"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <video 
                    src={exercise.video} 
                    controls
                    className="w-full h-full rounded-lg"
                  >
                    Your browser does not support the video tag.
                  </video>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button className="flex-1 btn-primary flex items-center justify-center gap-2 text-sm md:text-base py-2 md:py-3">
              <Play className="w-4 h-4" />
              Start Exercise
            </button>
            <button className="px-4 md:px-6 py-2 md:py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm md:text-base">
              Add to Workout
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExerciseDetail;
